/**
 * CR306: Theme Labs v2 - Global Design System Editor
 * 
 * =============================================================
 * THEME LABS = GLOBAL DESIGN TOKENS (applies across ALL cards)
 * =============================================================
 * 
 * This editor controls:
 * - Typography (fonts, sizes, line-height)
 * - Color palette (card colors, text colors, accents)
 * - Spacing (padding, gaps, header height)
 * - Component styling (ribbon, badge, button, starburst DEFAULTS)
 * - Theme presets (Light/Dark/Custom)
 * 
 * CR306 Updates:
 * - Tabs layout instead of accordion (Typography, Color Palette, Spacing, Component Defaults)
 * - Compact presets above tabs
 * - Icon tooltip instead of large explainer block
 * - Isolated CSS vars wrapper for preview
 * - Live from draft toggle for instant/persisted preview switching
 * - Increased vertical workspace (~820-900px before scroll)
 */

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { previewConfigStore } from "@/stores/previewConfigStore";
import { toast } from "sonner";
import {
  Palette,
  Sun,
  Moon,
  Sparkles,
  Eye,
  Save,
  Undo2,
  Type,
  Layers,
  Circle,
  LayoutGrid,
} from "lucide-react";
import { DealCardPreview } from "./DealCardPreview";
import { PreviewDebugPanel } from "./PreviewDebugPanel";
import { WiringDebugPanel } from "./WiringDebugPanel";
import { ThemePreviewWrapper } from "./ThemePreviewWrapper";
import { ThemeLabsExplainerTooltip } from "./ThemeLabsExplainerTooltip";
import { FontPicker } from "@/components/ui/font-picker";
import {
  type GlobalThemeTokens,
  type ThemePresetKey,
  type TypographyTokens,
  type ColorPaletteTokens,
  type SpacingTokens,
  type ComponentStyleTokens,
  type DealTypeThemeColors,
  DEFAULT_GLOBAL_THEME,
  DEFAULT_DEAL_TYPE_THEMES,
  THEME_PRESETS,
  hslToCss,
} from "@/lib/deals/globalThemeTokens";
import { DEFAULT_HEADING_FONT_ID, DEFAULT_BODY_FONT_ID } from "@/lib/theme/fontRegistry";
import { setLastPreviewAction } from "@/components/admin/preview";
import { PreviewErrorBoundary } from "@/components/admin/preview";
import { StarburstAssetPicker } from "./StarburstAssetPicker";
import { DealTypeThemesPanel } from "./DealTypeThemesPanel";
import { FoldLineControl } from "@/components/deals/FoldLineControl";
import { useFoldLineSettings } from "@/hooks/useFoldLineSettings";
import type { FoldLineHeight } from "@/components/deals/FoldLineOverlay";
// CR313: Admin preview is V2-only — remove rollout dependency

// ================================================================
// TYPES
// ================================================================

interface ThemeLabsTabProps {
  onRefresh?: () => void;
}

interface PresetConfig {
  name: string;
  icon: React.ReactNode;
}

const PRESET_CONFIGS: Record<ThemePresetKey, PresetConfig> = {
  light: {
    name: "Light",
    icon: <Sun className="h-4 w-4" />,
  },
  dark: {
    name: "Dark",
    icon: <Moon className="h-4 w-4" />,
  },
  custom: {
    name: "Custom",
    icon: <Sparkles className="h-4 w-4" />,
  },
};

// Sample deal types for multi-scenario preview
const PREVIEW_DEAL_TYPES = ["BONUS", "DISCOUNT", "POINTS", "IN_KIND"];

// ================================================================
// MAIN COMPONENT
// ================================================================

export function ThemeLabsTab({ onRefresh }: ThemeLabsTabProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Theme state - CR306: Clear draft vs persisted model
  const [activePreset, setActivePreset] = useState<ThemePresetKey>("light");
  const [draftTokens, setDraftTokens] = useState<GlobalThemeTokens>(DEFAULT_GLOBAL_THEME);
  const [persistedTokens, setPersistedTokens] = useState<GlobalThemeTokens>(DEFAULT_GLOBAL_THEME);
  const [isDirty, setIsDirty] = useState(false);

  // CR306: Live from draft toggle - controls whether preview uses draft or persisted
  const [liveFromDraft, setLiveFromDraft] = useState(true);

  // Preview panel state
  const [previewTab, setPreviewTab] = useState<"single" | "multi">("single");
  const [singlePreviewType, setSinglePreviewType] = useState("BONUS");
  const [previewError, setPreviewError] = useState<string | null>(null);
  
  // Active editor tab
  const [activeEditorTab, setActiveEditorTab] = useState("typography");
  
  // CR339: Deal Type Themes editor state
  const [dealTypeThemeSelection, setDealTypeThemeSelection] = useState("BONUS");

  // CR313: Admin preview is V2-only — hardcoded renderer
  
  // CR154: Use new fold line settings
  const { settings: foldSettings, setEnabled } = useFoldLineSettings();
  const foldHeight: FoldLineHeight = foldSettings.enabled ? "normal" : "off";
  const setFoldHeight = (h: FoldLineHeight) => setEnabled(h !== "off");
  
  // CR111D: Track last action for diagnostics
  const lastActionRef = useRef<string>("initial load");
  
  // CR208: Render tick counter for diagnostics
  const [renderTick, setRenderTick] = useState(0);
  
  const forcePreviewUpdate = useCallback(() => {
    setRenderTick(t => t + 1);
  }, []);

  // ================================================================
  // DATA LOADING
  // ================================================================

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // Load global theme tokens from app_config
        const { data: configData } = await supabase
          .from("app_config")
          .select("*")
          .eq("key", "global_theme_tokens")
          .single();

        if (configData?.value_json) {
          const tokens = configData.value_json as unknown as GlobalThemeTokens;
          const merged = { ...DEFAULT_GLOBAL_THEME, ...tokens };
          setPersistedTokens(merged);
          setDraftTokens(merged);
        }

        // Load active preset
        const { data: presetData } = await supabase
          .from("app_config")
          .select("*")
          .eq("key", "global_theme_preset")
          .single();

        if (presetData?.value_json) {
          const preset = (presetData.value_json as { preset: ThemePresetKey }).preset;
          setActivePreset(preset || "light");
        }
      } catch (err) {
        console.error("[ThemeLabsTab] Load error:", err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Track dirty state via deep compare
  useEffect(() => {
    setIsDirty(JSON.stringify(draftTokens) !== JSON.stringify(persistedTokens));
  }, [draftTokens, persistedTokens]);

  // CR173/CR208: Push draft tokens to unified preview store for real-time updates
  useEffect(() => {
    previewConfigStore.setGlobalTheme(draftTokens);
  }, [draftTokens]);

  // ================================================================
  // HANDLERS
  // ================================================================

  const handlePresetChange = useCallback((preset: ThemePresetKey) => {
    lastActionRef.current = `Changed preset to ${preset}`;
    setLastPreviewAction(`ThemeLabs: Changed preset to ${preset}`);
    setActivePreset(preset);
    if (preset !== "custom") {
      const presetTokens = THEME_PRESETS[preset];
      setDraftTokens((prev) => ({
        ...DEFAULT_GLOBAL_THEME,
        ...presetTokens,
        typography: { ...DEFAULT_GLOBAL_THEME.typography, ...presetTokens.typography },
        colors: { ...DEFAULT_GLOBAL_THEME.colors, ...presetTokens.colors },
        spacing: { ...DEFAULT_GLOBAL_THEME.spacing, ...presetTokens.spacing },
        components: { ...DEFAULT_GLOBAL_THEME.components, ...presetTokens.components },
      }));
    }
  }, []);

  const handleTypographyChange = useCallback((update: Partial<TypographyTokens>) => {
    const key = Object.keys(update)[0] || "typography";
    lastActionRef.current = `Changed ${key}`;
    setLastPreviewAction(`ThemeLabs: Changed ${key}`);
    setPreviewError(null);
    setDraftTokens((prev) => ({
      ...prev,
      typography: { ...prev.typography, ...update },
    }));
    if (activePreset !== "custom") setActivePreset("custom");
  }, [activePreset]);

  const handleColorsChange = useCallback((update: Partial<ColorPaletteTokens>) => {
    const key = Object.keys(update)[0] || "color";
    lastActionRef.current = `Changed ${key}`;
    setLastPreviewAction(`ThemeLabs: Changed ${key}`);
    setPreviewError(null);
    setDraftTokens((prev) => ({
      ...prev,
      colors: { ...prev.colors, ...update },
    }));
    if (activePreset !== "custom") setActivePreset("custom");
  }, [activePreset]);

  const handleSpacingChange = useCallback((update: Partial<SpacingTokens>) => {
    const key = Object.keys(update)[0] || "spacing";
    lastActionRef.current = `Changed ${key}`;
    setLastPreviewAction(`ThemeLabs: Changed ${key}`);
    setPreviewError(null);
    setDraftTokens((prev) => ({
      ...prev,
      spacing: { ...prev.spacing, ...update },
    }));
    if (activePreset !== "custom") setActivePreset("custom");
  }, [activePreset]);

  const handleComponentsChange = useCallback((update: Partial<ComponentStyleTokens>) => {
    const key = Object.keys(update)[0] || "component";
    lastActionRef.current = `Changed ${key}`;
    setLastPreviewAction(`ThemeLabs: Changed ${key}`);
    setPreviewError(null);
    setDraftTokens((prev) => ({
      ...prev,
      components: { ...prev.components, ...update },
    }));
    if (activePreset !== "custom") setActivePreset("custom");
  }, [activePreset]);

  // CR339: Handler for deal type theme changes
  const handleDealTypeThemesChange = useCallback((themes: Record<string, DealTypeThemeColors>) => {
    lastActionRef.current = "Changed deal type theme";
    setLastPreviewAction("ThemeLabs: Changed deal type theme");
    setPreviewError(null);
    setDraftTokens((prev) => ({
      ...prev,
      dealTypeThemes: themes,
    }));
    if (activePreset !== "custom") setActivePreset("custom");
  }, [activePreset]);

  const handleDiscard = useCallback(() => {
    lastActionRef.current = "Discarded changes";
    setDraftTokens(persistedTokens);
    setIsDirty(false);
    setPreviewError(null);
    toast.info("Changes discarded");
  }, [persistedTokens]);

  const handleRetryPreview = useCallback(() => {
    lastActionRef.current = "Retry preview";
    setLastPreviewAction("ThemeLabs: Retry preview");
    forcePreviewUpdate();
    setPreviewError(null);
  }, [forcePreviewUpdate]);

  const handleResetToSaved = useCallback(() => {
    lastActionRef.current = "Reset to saved";
    setLastPreviewAction("ThemeLabs: Reset to saved");
    setDraftTokens(persistedTokens);
    setIsDirty(false);
    setPreviewError(null);
    toast.info("Draft reset to saved tokens");
  }, [persistedTokens]);

  const handlePreviewError = useCallback((error: Error) => {
    console.error("[ThemeLabsTab] Preview error:", error);
    setPreviewError(error.message || "Unknown preview error");
    setLastPreviewAction(`Preview error: ${error.message}`);
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      // Save tokens to app_config - use delete + insert pattern for upsert
      await supabase.from("app_config").delete().eq("key", "global_theme_tokens");
      const { error: tokensError } = await supabase.from("app_config").insert([
        {
          key: "global_theme_tokens",
          value_json: JSON.parse(JSON.stringify(draftTokens)),
        },
      ]);

      if (tokensError) throw tokensError;

      // Save preset selection
      await supabase.from("app_config").delete().eq("key", "global_theme_preset");
      const { error: presetError } = await supabase.from("app_config").insert([
        {
          key: "global_theme_preset",
          value_json: { preset: activePreset },
        },
      ]);

      if (presetError) throw presetError;

      setPersistedTokens(draftTokens);
      setIsDirty(false);
      onRefresh?.();
      toast.success("Theme tokens saved globally");
    } catch (err) {
      console.error("[ThemeLabsTab] Save error:", err);
      toast.error("Failed to save theme tokens");
    } finally {
      setSaving(false);
    }
  }, [draftTokens, activePreset, onRefresh]);

  // ================================================================
  // COMPUTED VALUES
  // ================================================================

  // Tokens to use for preview based on liveFromDraft toggle
  const previewTokens = liveFromDraft ? draftTokens : persistedTokens;

  const previewStyles = useMemo(() => ({
    cardBackground: hslToCss(previewTokens.colors.cardBackground),
    cardBorder: hslToCss(previewTokens.colors.cardBorder),
    headerBackground: hslToCss(previewTokens.colors.headerBackground),
    headerText: hslToCss(previewTokens.colors.headerText),
    bodyText: hslToCss(previewTokens.colors.bodyText),
    cardRadius: `${previewTokens.components.cardCornerRadius}px`,
    cardBorderWidth: `${previewTokens.components.cardBorderWidth}px`,
  }), [previewTokens]);

  // Draft hash for diagnostics
  const draftHash = useMemo(() => {
    const fullTokenJson = JSON.stringify(draftTokens);
    let hash = 0;
    for (let i = 0; i < fullTokenJson.length; i++) {
      const char = fullTokenJson.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(16).padStart(8, '0');
  }, [draftTokens]);

  const [previewVersion, setPreviewVersion] = useState(0);
  useEffect(() => {
    setPreviewVersion(v => v + 1);
  }, [previewTokens]);

  const previewKey = useMemo(() => {
    return `v${previewVersion}-r${renderTick}-${draftHash}-${liveFromDraft ? 'draft' : 'persisted'}`;
  }, [previewVersion, renderTick, draftHash, liveFromDraft]);

  // ================================================================
  // RENDER
  // ================================================================

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="flex flex-col" style={{ minHeight: "calc(100vh - 180px)" }}>
      {/* Header Row - Compact */}
      <div className="flex items-center justify-between pb-4 border-b mb-4">
        <div className="flex items-center gap-3">
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Palette className="h-5 w-5 text-primary" />
              Theme Labs
              <Badge variant="outline" className="text-xs font-normal">Global</Badge>
              <ThemeLabsExplainerTooltip />
            </h2>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isDirty && (
            <Badge variant="secondary" className="gap-1 text-xs">
              Unsaved
            </Badge>
          )}
          <Button variant="outline" size="sm" onClick={handleDiscard} disabled={!isDirty || saving}>
            <Undo2 className="h-4 w-4 mr-1" />
            Discard
          </Button>
          <Button size="sm" onClick={handleSave} disabled={!isDirty || saving}>
            <Save className="h-4 w-4 mr-1" />
            {saving ? "Saving..." : "Save Tokens"}
          </Button>
        </div>
      </div>

      {/* Main Content: Two Columns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 flex-1 min-h-0">
        {/* Left: Token Editor */}
        <div className="flex flex-col min-h-0">
          {/* Theme Preset Selection - Compact row */}
          <div className="flex items-center gap-2 mb-4">
            <span className="text-sm font-medium text-muted-foreground">Preset:</span>
            <div className="flex gap-1">
              {(Object.entries(PRESET_CONFIGS) as [ThemePresetKey, PresetConfig][]).map(([key, config]) => (
                <Button
                  key={key}
                  variant={activePreset === key ? "default" : "outline"}
                  size="sm"
                  onClick={() => handlePresetChange(key)}
                  className="gap-1.5"
                >
                  {config.icon}
                  {config.name}
                </Button>
              ))}
            </div>
          </div>

          {/* Tabs for Token Categories */}
          <Tabs value={activeEditorTab} onValueChange={setActiveEditorTab} className="flex-1 flex flex-col min-h-0">
            <TabsList className="grid grid-cols-4 mb-4">
              <TabsTrigger value="typography" className="text-xs gap-1">
                <Type className="h-3.5 w-3.5" />
                Typography
              </TabsTrigger>
              <TabsTrigger value="colors" className="text-xs gap-1">
                <Circle className="h-3.5 w-3.5" />
                Colors
              </TabsTrigger>
              <TabsTrigger value="spacing" className="text-xs gap-1">
                <LayoutGrid className="h-3.5 w-3.5" />
                Spacing
              </TabsTrigger>
              <TabsTrigger value="components" className="text-xs gap-1">
                <Layers className="h-3.5 w-3.5" />
                Components
              </TabsTrigger>
            </TabsList>

            {/* Typography Tab */}
            <TabsContent value="typography" className="flex-1 overflow-auto">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm">Typography Tokens</CardTitle>
                  <CardDescription className="text-xs">Fonts, scale, and line height</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs">Heading Font</Label>
                      <FontPicker
                        value={draftTokens.typography.headingFontId ?? DEFAULT_HEADING_FONT_ID}
                        onChange={(fontId) => handleTypographyChange({ headingFontId: fontId })}
                        size="sm"
                        showPreview
                      />
                      <p className="text-[10px] text-muted-foreground">Titles and headlines</p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Body Font</Label>
                      <FontPicker
                        value={draftTokens.typography.bodyFontId ?? DEFAULT_BODY_FONT_ID}
                        onChange={(fontId) => handleTypographyChange({ bodyFontId: fontId })}
                        size="sm"
                        showPreview
                      />
                      <p className="text-[10px] text-muted-foreground">Descriptions and content</p>
                    </div>
                  </div>
                  <Separator />
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs">Font Scale</Label>
                      <Select
                        value={draftTokens.typography.fontScale}
                        onValueChange={(v) => handleTypographyChange({ fontScale: v as TypographyTokens["fontScale"] })}
                      >
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="compact">Compact</SelectItem>
                          <SelectItem value="normal">Normal</SelectItem>
                          <SelectItem value="large">Large</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Line Height</Label>
                      <Select
                        value={draftTokens.typography.lineHeight}
                        onValueChange={(v) => handleTypographyChange({ lineHeight: v as TypographyTokens["lineHeight"] })}
                      >
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="tight">Tight</SelectItem>
                          <SelectItem value="normal">Normal</SelectItem>
                          <SelectItem value="relaxed">Relaxed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Colors Tab */}
            <TabsContent value="colors" className="flex-1 overflow-auto">
              <div className="space-y-4">
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-sm">Color Palette</CardTitle>
                        <CardDescription className="text-xs">Card, header, text, and accent colors (HSL format)</CardDescription>
                      </div>
                      <a
                        href="https://www.figma.com/colors/"
                        target="_blank"
                        rel="noreferrer noopener"
                        className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline flex items-center gap-1"
                      >
                        Figma Colors ↗
                      </a>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { key: "cardBackground", label: "Card Background" },
                        { key: "cardBorder", label: "Card Border" },
                        { key: "headerBackground", label: "Header Background" },
                        { key: "headerText", label: "Header Text" },
                        { key: "bodyText", label: "Body Text" },
                        { key: "mutedText", label: "Muted Text" },
                        { key: "accentColor", label: "Accent Color" },
                      ].map(({ key, label }) => (
                        <div key={key} className="space-y-1.5">
                          <Label className="text-xs flex items-center gap-2">
                            <div
                              className="w-4 h-4 rounded border"
                              style={{ backgroundColor: hslToCss(draftTokens.colors[key as keyof ColorPaletteTokens]) }}
                            />
                            {label}
                          </Label>
                          <Input
                            className="h-8 text-xs font-mono"
                            value={draftTokens.colors[key as keyof ColorPaletteTokens]}
                            onChange={(e) => handleColorsChange({ [key]: e.target.value })}
                            placeholder="H S% L%"
                          />
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      HSL: "220 60% 50%" or with alpha "0 0% 0% / 0.1"
                    </p>
                  </CardContent>
                </Card>

                {/* CR339: Deal Type Themes */}
                <DealTypeThemesPanel
                  selectedDealType={dealTypeThemeSelection}
                  onSelectDealType={setDealTypeThemeSelection}
                  dealTypeThemes={draftTokens.dealTypeThemes ?? DEFAULT_DEAL_TYPE_THEMES}
                  onChange={handleDealTypeThemesChange}
                />
              </div>
            </TabsContent>

            {/* Spacing Tab */}
            <TabsContent value="spacing" className="flex-1 overflow-auto">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm">Spacing Tokens</CardTitle>
                  <CardDescription className="text-xs">Padding, gaps, and dimensions</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs">Card Padding</Label>
                      <Select
                        value={draftTokens.spacing.cardPadding}
                        onValueChange={(v) => handleSpacingChange({ cardPadding: v as SpacingTokens["cardPadding"] })}
                      >
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="compact">Compact</SelectItem>
                          <SelectItem value="normal">Normal</SelectItem>
                          <SelectItem value="spacious">Spacious</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Section Gap</Label>
                      <Select
                        value={draftTokens.spacing.sectionGap}
                        onValueChange={(v) => handleSpacingChange({ sectionGap: v as SpacingTokens["sectionGap"] })}
                      >
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="tight">Tight</SelectItem>
                          <SelectItem value="normal">Normal</SelectItem>
                          <SelectItem value="relaxed">Relaxed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Header Height</Label>
                      <Select
                        value={draftTokens.spacing.headerHeight}
                        onValueChange={(v) => handleSpacingChange({ headerHeight: v as SpacingTokens["headerHeight"] })}
                      >
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="sm">Small</SelectItem>
                          <SelectItem value="md">Medium</SelectItem>
                          <SelectItem value="lg">Large</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Component Defaults Tab */}
            <TabsContent value="components" className="flex-1 overflow-auto">
              <ScrollArea className="h-[calc(100vh-400px)] pr-4">
                <div className="space-y-4">
                  {/* Card Container - CR311: Moved from Deal Cards > Styling */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Card Container</CardTitle>
                      <CardDescription className="text-xs">Global card styling (applies to all deal types)</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label className="text-xs text-muted-foreground">Corner Radius</Label>
                          <div className="flex items-center gap-2">
                            <Slider
                              value={[draftTokens.components.cardCornerRadius]}
                              onValueChange={([v]) => handleComponentsChange({ cardCornerRadius: v })}
                              min={0}
                              max={32}
                              step={2}
                              className="flex-1"
                            />
                            <span className="text-xs w-8 text-right">{draftTokens.components.cardCornerRadius}px</span>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs text-muted-foreground">Shadow</Label>
                          <Select
                            value={draftTokens.components.cardShadow}
                            onValueChange={(v) => handleComponentsChange({ cardShadow: v as ComponentStyleTokens["cardShadow"] })}
                          >
                            <SelectTrigger className="h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">None</SelectItem>
                              <SelectItem value="low">Low</SelectItem>
                              <SelectItem value="medium">Medium</SelectItem>
                              <SelectItem value="high">High</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs text-muted-foreground">Border Width</Label>
                          <div className="flex items-center gap-2">
                            <Slider
                              value={[draftTokens.components.cardBorderWidth]}
                              onValueChange={([v]) => handleComponentsChange({ cardBorderWidth: v })}
                              min={0}
                              max={4}
                              step={1}
                              className="flex-1"
                            />
                            <span className="text-xs w-8 text-right">{draftTokens.components.cardBorderWidth}px</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* CR311: Style Preview swatch (moved from Deal Cards > Styling) */}
                      <div className="pt-3 border-t">
                        <Label className="text-xs text-muted-foreground mb-2 block">Style Preview</Label>
                        <div 
                          className="h-16 w-full transition-all"
                          style={{
                            borderRadius: `${draftTokens.components.cardCornerRadius}px`,
                            background: hslToCss(draftTokens.colors.cardBackground),
                            border: `${draftTokens.components.cardBorderWidth}px solid ${hslToCss(draftTokens.colors.cardBorder)}`,
                            boxShadow: draftTokens.components.cardShadow === "none" ? "none" 
                              : draftTokens.components.cardShadow === "low" ? "0 4px 12px rgba(0,0,0,0.04)"
                              : draftTokens.components.cardShadow === "medium" ? "0 10px 30px rgba(0,0,0,0.08)"
                              : "0 20px 50px rgba(0,0,0,0.15)",
                          }}
                        />
                      </div>
                    </CardContent>
                  </Card>

                  {/* Badges/Pills */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Badges / Pills</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label className="text-xs text-muted-foreground">Height</Label>
                          <Select
                            value={draftTokens.components.badge.height}
                            onValueChange={(v) =>
                              handleComponentsChange({
                                badge: { ...draftTokens.components.badge, height: v as "sm" | "md" | "lg" },
                              })
                            }
                          >
                            <SelectTrigger className="h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="sm">Small</SelectItem>
                              <SelectItem value="md">Medium</SelectItem>
                              <SelectItem value="lg">Large</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs text-muted-foreground">Radius</Label>
                          <Select
                            value={draftTokens.components.badge.borderRadius}
                            onValueChange={(v) =>
                              handleComponentsChange({
                                badge: { ...draftTokens.components.badge, borderRadius: v as "none" | "sm" | "md" | "full" },
                              })
                            }
                          >
                            <SelectTrigger className="h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">None</SelectItem>
                              <SelectItem value="sm">Small</SelectItem>
                              <SelectItem value="md">Medium</SelectItem>
                              <SelectItem value="full">Full</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs text-muted-foreground">Border</Label>
                          <Select
                            value={String(draftTokens.components.badge.borderWidth)}
                            onValueChange={(v) =>
                              handleComponentsChange({
                                badge: { ...draftTokens.components.badge, borderWidth: Number(v) as 0 | 1 | 2 },
                              })
                            }
                          >
                            <SelectTrigger className="h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="0">None</SelectItem>
                              <SelectItem value="1">1px</SelectItem>
                              <SelectItem value="2">2px</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Buttons */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Buttons</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-xs text-muted-foreground">Radius</Label>
                          <Select
                            value={draftTokens.components.button.borderRadius}
                            onValueChange={(v) =>
                              handleComponentsChange({
                                button: { ...draftTokens.components.button, borderRadius: v as "none" | "sm" | "md" | "lg" | "full" },
                              })
                            }
                          >
                            <SelectTrigger className="h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">None</SelectItem>
                              <SelectItem value="sm">Small</SelectItem>
                              <SelectItem value="md">Medium</SelectItem>
                              <SelectItem value="lg">Large</SelectItem>
                              <SelectItem value="full">Full</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs text-muted-foreground">Size</Label>
                          <Select
                            value={draftTokens.components.button.size}
                            onValueChange={(v) =>
                              handleComponentsChange({
                                button: { ...draftTokens.components.button, size: v as "sm" | "md" | "lg" },
                              })
                            }
                          >
                            <SelectTrigger className="h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="sm">Small</SelectItem>
                              <SelectItem value="md">Medium</SelectItem>
                              <SelectItem value="lg">Large</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Starburst */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Starburst Badge</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <StarburstAssetPicker
                        selectedAssetId={draftTokens.components.starburst.assetId || null}
                        starburstEnabled={true}
                        onSelectAsset={(assetId) => handleComponentsChange({
                          starburst: { ...draftTokens.components.starburst, assetId: assetId ?? undefined },
                        })}
                      />
                      <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label className="text-xs text-muted-foreground">Size</Label>
                          <Select
                            value={draftTokens.components.starburst.size}
                            onValueChange={(v) =>
                              handleComponentsChange({
                                starburst: { ...draftTokens.components.starburst, size: v as "sm" | "md" | "lg" },
                              })
                            }
                          >
                            <SelectTrigger className="h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="sm">Small</SelectItem>
                              <SelectItem value="md">Medium</SelectItem>
                              <SelectItem value="lg">Large</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs text-muted-foreground">Offset X</Label>
                          <div className="flex items-center gap-2">
                            <Slider
                              value={[draftTokens.components.starburst.offsetX]}
                              onValueChange={([v]) =>
                                handleComponentsChange({
                                  starburst: { ...draftTokens.components.starburst, offsetX: v },
                                })
                              }
                              min={-50}
                              max={50}
                              step={2}
                              className="flex-1"
                            />
                            <span className="text-xs w-10 text-right">{draftTokens.components.starburst.offsetX}px</span>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs text-muted-foreground">Offset Y</Label>
                          <div className="flex items-center gap-2">
                            <Slider
                              value={[draftTokens.components.starburst.offsetY]}
                              onValueChange={([v]) =>
                                handleComponentsChange({
                                  starburst: { ...draftTokens.components.starburst, offsetY: v },
                                })
                              }
                              min={-50}
                              max={50}
                              step={2}
                              className="flex-1"
                            />
                            <span className="text-xs w-10 text-right">{draftTokens.components.starburst.offsetY}px</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Merchant Name */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Merchant Name</CardTitle>
                      <CardDescription className="text-xs">Where the merchant name appears</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-xs text-muted-foreground">Placement</Label>
                          <Select
                            value={draftTokens.components.merchantName?.placement || "HEADER_TITLE"}
                            onValueChange={(v) =>
                              handleComponentsChange({
                                merchantName: { 
                                  ...draftTokens.components.merchantName,
                                  placement: v as "HIDDEN" | "HEADER_TITLE" | "BODY_TOP" | "BODY_BELOW_CHIPS" | "BODY_ABOVE_DETAILS" | "BODY_FOOTER",
                                },
                              })
                            }
                          >
                            <SelectTrigger className="h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="HIDDEN">Hidden</SelectItem>
                              <SelectItem value="HEADER_TITLE">Header Title</SelectItem>
                              <SelectItem value="BODY_TOP">Body (Top)</SelectItem>
                              <SelectItem value="BODY_BELOW_CHIPS">Body (Below Chips)</SelectItem>
                              <SelectItem value="BODY_ABOVE_DETAILS">Body (Above Details)</SelectItem>
                              <SelectItem value="BODY_FOOTER">Body (Footer)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs text-muted-foreground">Size</Label>
                          <Select
                            value={draftTokens.components.merchantName?.style || "TITLE"}
                            onValueChange={(v) =>
                              handleComponentsChange({
                                merchantName: { 
                                  ...draftTokens.components.merchantName,
                                  style: v as "TITLE" | "SUBTITLE" | "SMALL",
                                },
                              })
                            }
                          >
                            <SelectTrigger className="h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="TITLE">Title</SelectItem>
                              <SelectItem value="SUBTITLE">Subtitle</SelectItem>
                              <SelectItem value="SMALL">Small</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </ScrollArea>
            </TabsContent>
          </Tabs>
        </div>

        {/* Right: Live Preview */}
        <div className="flex flex-col min-h-0">
          <Card className="flex-1 flex flex-col min-h-0">
            <CardHeader className="pb-3 shrink-0">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Eye className="h-4 w-4" />
                  Preview
                  {/* CR313: Admin preview is always V2 */}
                  <Badge variant="default" className="text-xs">V2</Badge>
                </CardTitle>
                <div className="flex items-center gap-3">
                  {/* Live from draft toggle */}
                  <div className="flex items-center gap-2">
                    <Switch 
                      id="live-draft" 
                      checked={liveFromDraft}
                      onCheckedChange={setLiveFromDraft}
                      className="scale-90"
                    />
                    <Label htmlFor="live-draft" className="text-xs text-muted-foreground cursor-pointer">
                      {liveFromDraft ? "Draft" : "Saved"}
                    </Label>
                  </div>
                  <Tabs value={previewTab} onValueChange={(v) => setPreviewTab(v as "single" | "multi")}>
                    <TabsList className="h-7">
                      <TabsTrigger value="single" className="text-xs px-2 h-6">Single</TabsTrigger>
                      <TabsTrigger value="multi" className="text-xs px-2 h-6">All</TabsTrigger>
                    </TabsList>
                  </Tabs>
                </div>
              </div>
              
              {/* Fold line control + debug panels */}
              <div className="flex items-center gap-4 mt-2 flex-wrap">
                <FoldLineControl 
                  value={foldHeight} 
                  onChange={setFoldHeight}
                  compact
                />
                {/* CR313: Wiring Debug Panel - V2 only */}
                <WiringDebugPanel
                  renderer="v2"
                  isRendererForced={true}
                  tokenSource={liveFromDraft ? "draft" : "persisted"}
                  draftTokens={draftTokens}
                  persistedTokens={persistedTokens}
                  hasChanges={isDirty}
                />
                <PreviewDebugPanel
                  previewMode={previewTab}
                  selectedDealType={previewTab === "single" ? singlePreviewType : undefined}
                  tokenSource={liveFromDraft ? "draft" : "saved"}
                  draftTokens={draftTokens}
                  savedTokens={persistedTokens}
                  hasChanges={isDirty}
                  lastAction={lastActionRef.current}
                  previewError={previewError}
                  onRetry={handleRetryPreview}
                  onResetToSaved={handleResetToSaved}
                  debugInfo={{ previewKey, activePreset, draftHash, renderTick: renderTick + previewVersion }}
                />
              </div>
            </CardHeader>
            
            <CardContent className="flex-1 overflow-auto pt-0">
              <PreviewErrorBoundary
                previewContext="ThemeLabs"
                onReset={handleRetryPreview}
              >
                {/* CR306: Isolated theme preview wrapper with CSS vars */}
                <ThemePreviewWrapper 
                  tokens={previewTokens} 
                  dataSource={liveFromDraft ? "draft" : "persisted"}
                >
                  {previewTab === "single" ? (
                    <div className="space-y-3">
                      <Select value={singlePreviewType} onValueChange={setSinglePreviewType}>
                        <SelectTrigger className="w-32 h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {PREVIEW_DEAL_TYPES.map((dt) => (
                            <SelectItem key={dt} value={dt}>
                              {dt}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div key={previewKey} className="bg-muted/30 rounded-lg p-4">
                        <DealCardPreview
                          dealType={singlePreviewType}
                          globalThemeOverride={previewTokens}
                          previewContext="ThemeLabs-single"
                          lastAction={lastActionRef.current}
                          onPreviewReset={handleRetryPreview}
                          debugScreen="theme_labs"
                        />
                      </div>
                    </div>
                  ) : (
                    <ScrollArea className="h-[450px]">
                      <div className="space-y-4 pr-4" key={previewKey}>
                        {PREVIEW_DEAL_TYPES.map((dealType) => (
                          <div key={dealType} className="space-y-2">
                            <Badge variant="outline" className="text-xs">
                              {dealType}
                            </Badge>
                            <div className="bg-muted/30 rounded-lg p-3">
                              <DealCardPreview
                                dealType={dealType}
                                globalThemeOverride={previewTokens}
                                previewContext={`ThemeLabs-multi-${dealType}`}
                                lastAction={lastActionRef.current}
                                onPreviewReset={handleRetryPreview}
                                debugScreen="theme_labs"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </ThemePreviewWrapper>

                {/* Token Summary */}
                <Separator className="my-4" />
                <div className="grid grid-cols-4 gap-2">
                  <div className="text-center">
                    <div
                      className="w-8 h-8 rounded mx-auto border"
                      style={{ backgroundColor: previewStyles.cardBackground }}
                    />
                    <p className="text-[10px] text-muted-foreground mt-1">Card BG</p>
                  </div>
                  <div className="text-center">
                    <div
                      className="w-8 h-8 rounded mx-auto border"
                      style={{ backgroundColor: previewStyles.headerBackground }}
                    />
                    <p className="text-[10px] text-muted-foreground mt-1">Header</p>
                  </div>
                  <div className="text-center">
                    <div className="w-8 h-8 rounded mx-auto border flex items-center justify-center text-xs font-bold">
                      {previewTokens.components.cardCornerRadius}
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-1">Radius</p>
                  </div>
                  <div className="text-center">
                    <div className="w-8 h-8 rounded mx-auto border flex items-center justify-center text-xs capitalize">
                      {previewTokens.components.cardShadow[0]}
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-1">Shadow</p>
                  </div>
                </div>
              </PreviewErrorBoundary>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
